import { Modal } from '../ui/Modal';
import { Link } from 'react-router-dom';
import { accountDetailPath } from '../../lib/portal';
import { formatPhoneDisplay, phoneTelHref } from '../../lib/intlPhone';
import { useCrmData } from '../../contexts/CrmDataContext';
import type { Client, Contact } from '../../types/crm';
import { clientById, contactLabel, formatClientSalespersonCode } from '../../utils/crmHelpers';
import type { DedupedAccountContact } from '../../utils/contactDedup';

interface ContactDetailModalProps {
  open: boolean;
  entry: DedupedAccountContact | null;
  /** Linked accounts when opening from the Contacts list. */
  accounts?: Client[];
  /** Account salesperson code when viewing from an account page. */
  salespersonCode?: string;
  /** IT Management — omit salesperson field. */
  hideSalesperson?: boolean;
  canEdit: boolean;
  onClose: () => void;
  onEdit: (contact: Contact) => void;
}

function displayValue(value?: string | null): string {
  const trimmed = value?.trim();
  return trimmed || '—';
}

function telHref(phone: string): string {
  return phoneTelHref(phone) || `tel:${phone}`;
}

export function ContactDetailModal({
  open,
  entry,
  accounts = [],
  salespersonCode,
  hideSalesperson = false,
  canEdit,
  onClose,
  onEdit,
}: ContactDetailModalProps) {
  const { data } = useCrmData();
  if (!entry) return null;

  const { contact, brandNames } = entry;
  const email = contact.email?.trim() ?? '';
  const phone = contact.phone?.trim() ?? '';
  const fromAccount =
    salespersonCode?.trim() ||
    formatClientSalespersonCode(clientById(data.clients, contact.client_id)?.owner_id);
  const linkedAccounts =
    accounts.length > 0
      ? accounts
      : (() => {
          const linked = clientById(data.clients, contact.client_id);
          return linked ? [linked] : [];
        })();

  return (
    <Modal title="Contact details" open={open} onClose={onClose}>
      <div className="task-detail-modal contact-detail-modal">
        <h3 className="task-detail-modal__title">{contactLabel(contact)}</h3>

        <dl className="task-detail-modal__meta">
          <dt>Job title</dt>
          <dd>{displayValue(contact.job_title)}</dd>

          <dt>Email</dt>
          <dd>
            {email ? (
              <a href={`mailto:${email}`} className="account-side-link">
                {email}
              </a>
            ) : (
              '—'
            )}
          </dd>

          <dt>Phone</dt>
          <dd>
            {phone ? (
              <a href={telHref(phone)} className="account-side-link">
                {formatPhoneDisplay(phone)}
              </a>
            ) : (
              '—'
            )}
          </dd>

          {linkedAccounts.length > 0 ? (
            <>
              <dt>Account{linkedAccounts.length > 1 ? 's' : ''}</dt>
              <dd>
                {linkedAccounts.map((account, idx) => (
                  <span key={account.id}>
                    {idx > 0 ? ' · ' : null}
                    <Link to={accountDetailPath(account.code)} className="account-side-link">
                      {account.company_name}
                    </Link>
                    {account.code ? ` (${account.code})` : ''}
                  </span>
                ))}
              </dd>
            </>
          ) : brandNames.length > 0 ? (
            <>
              <dt>Brands</dt>
              <dd>{brandNames.join(' · ')}</dd>
            </>
          ) : null}

          {!hideSalesperson ? (
            <>
              <dt>Salesperson</dt>
              <dd className="mono">{fromAccount}</dd>
            </>
          ) : null}
        </dl>

        {canEdit ? (
          <div className="task-detail-modal__actions">
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                onClose();
                onEdit(contact);
              }}
            >
              Edit contact
            </button>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
